import { healthStore } from './healthStore';
import { checkApiHealth } from '@/lib/api/health';

interface HealthCheckOptions {
  force?: boolean;
  onSuccess?: (healthy: boolean) => void;
  useApi?: boolean; // Option to use API instead of WebSocket
  timeoutMs?: number; // Timeout option to prevent getting stuck
}

/**
 * Function to perform a health check using API
 * 
 * @param options Options for the health check
 * @returns Promise that resolves to the health status or null if checking
 */
export const performHealthCheck = async (options: HealthCheckOptions = {}): Promise<boolean | null> => {
  const { force = false, onSuccess, useApi = true, timeoutMs = 10000 } = options;
  
  // If we're already checking and not forcing, don't check again
  if (healthStore.isChecking() && !force) {
    console.log('Health check already in progress, skipping');
    return null;
  }
  
  // Mark as checking
  healthStore.setIsChecking(true);
  
  // Use API for health checks (default behavior)
  if (useApi) {
    try {
      // Store current timestamp for response time calculation
      healthStore.setLastRequestTimestamp(Date.now());
      
      const healthData = await Promise.race([
        checkApiHealth(),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Health check timeout')), timeoutMs);
        })
      ]) as any;
      
      // Calculate response time
      const responseTime = Date.now() - (healthStore.getLastRequestTimestamp() || Date.now());
      healthStore.setLastResponseTime(responseTime);
      
      const isHealthy = healthData && healthData.status === 'ok';
      healthStore.setHealthStatus(isHealthy);
      
      // Add response time to health data
      if (healthData && typeof healthData === 'object') {
        healthData.websocket_latency = responseTime;
      }
      
      healthStore.setHealthData(healthData);
      healthStore.setIsChecking(false);
      
      if (onSuccess) {
        onSuccess(isHealthy);
      }
      
      return isHealthy;
    } catch (error) {
      console.error('Health check error:', error);
      healthStore.setIsChecking(false);
      
      if (error.message === 'Health check timeout') {
        return null;
      }
      
      // For other errors, set health status to false
      healthStore.setHealthStatus(false);
      return false;
    }
  }
  
  // Otherwise, we'll just return the current status
  healthStore.setIsChecking(false);
  return healthStore.getHealthStatus();
};
